/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import DS from 'ember-data';
import ApiService from 'cio/services/api';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import SubscriptionCenterService from 'cio/services/subscription-center';
import AlertsService from 'cio/services/alerts';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import { Language, subscriptionCenterTranslatedLanguages } from 'cio/utils/languages';

interface SubscriptionCenterAddLanguagesButtonArgs {
  onAdd?: (languages: string[]) => unknown;
}

export default class SubscriptionCenterAddLanguagesButton extends Component<SubscriptionCenterAddLanguagesButtonArgs> {
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;
  @service subscriptionCenter!: SubscriptionCenterService;

  @tracked isAddLanguageModalOpen = false;
  @tracked isFeedbackModalOpen = false;
  @tracked alreadySelectedLanguages: string[] = [];
  @tracked languagesToAdd: string[] = [];

  constructor(owner: unknown, args: never) {
    super(owner, args);

    this.subscriptionCenter.initializeTask.perform();
  }

  get isAddButtonDisabled() {
    if (!this.subscriptionCenter.initialized) {
      return true;
    }

    return this.onAdd.isRunning || this.subscriptionCenter.hasNoTopics;
  }

  get availableLanguages(): Language[] {
    return subscriptionCenterTranslatedLanguages();
  }

  @action
  toggleAddLanguageModal() {
    if (!this.isAddLanguageModalOpen) {
      this.analytics.track('subscription_center', 'add_languages_started');
    }

    this.alreadySelectedLanguages = this.store.peekAll('subscription-language').map((lang) => lang.id.toLowerCase());
    this.isAddLanguageModalOpen = !this.isAddLanguageModalOpen;
    this.languagesToAdd = [];
  }

  onAdd = dropTask(async (languages: string[]) => {
    try {
      let models = languages.map((id) => this.store.createRecord('subscriptionLanguage', { id }));
      let modelsSerialized = models.map((m) => m.serialize({ includeId: true }));
      models.forEach((m) => m.unloadRecord()); // these records aren't saved yet, unload them so they don't mess with .pushPayload later

      let newLanguages = await this.api.post('subscription_languages', {
        data: { subscription_languages: modelsSerialized },
      });

      this.store.pushPayload(newLanguages);
      this.subscriptionCenter.fetchLanguages();
      this.isAddLanguageModalOpen = false;
      this.alerts.success(
        'subscription-center.localization.languages.added.success',
        `${formatNumberAndPluralize([languages.length, 'language'])} added`,
      );
      this.args.onAdd?.(languages);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  @action
  onOpenFeedbackModal() {
    this.isAddLanguageModalOpen = false;
    this.isFeedbackModalOpen = true;
  }

  @action
  closeAndReOpenAddLanguageModal() {
    this.isFeedbackModalOpen = false;
    this.isAddLanguageModalOpen = true;
  }

  @action
  onPersistTemporaryProgress(languages: string[]) {
    this.languagesToAdd = languages;
  }
}
